import { useRouter } from "next/router";
import PropTypes from "prop-types";

import {
  actionNames,
  actionPrefixes,
  objectTypes,
  useTracker,
} from "@newsela/snitch";
import { LANG_TOGGLE_LIST } from "@/constants/Language";
import PanelHeader from "@/view/PanelHeader";
import useReadingSkillsContext from "@/view/utils/useReadingSkillsContext/useReadingSkillsContext";
import { Tabs } from "@/components/Tabs";
import quizQueryParams from "../constants/quizQueryParams";
import QuizOverviewContainer from "./QuizOverviewContainer";
import QuizPreviewContainer from "./QuizPreviewContainer";
import {
  headerTextContainerCss,
  headerTextCss,
  subHeaderTextCss,
} from "./ReadingSkillsQuizContainer.style";

const ELA_QUIZ_SUBHEADER = "ELA";
const QUIZ_OVERVIEW_PARAM = quizQueryParams.QUIZ_OVERVIEW_PARAM;
const QUIZ_PREVIEW_PARAM = quizQueryParams.QUIZ_PREVIEW_PARAM;

const propTypes = {
  closeButtonEventProperties: PropTypes.object,
  data: PropTypes.object,
  isPresentModeActive: PropTypes.bool,
  onClickCloseButton: PropTypes.func,
  shouldFocusCloseButtonRef: PropTypes.object,
  language: PropTypes.oneOf(LANG_TOGGLE_LIST),
};

const ReadingSkillsQuizContainer = ({
  closeButtonEventProperties,
  data,
  isPresentModeActive,
  onClickCloseButton,
  shouldFocusCloseButtonRef,
  language,
}) => {
  const { trackAction } = useTracker();
  const router = useRouter();
  const {
    pathname,
    query,
    query: { activityPanel, levelId, quizId },
  } = router;

  const { quizQuestionMetadata } = useReadingSkillsContext();

  const onClickBackButton = () => {
    const query = { ...router.query };
    // Remove all query params associated with the quiz.
    delete query.activityPanel;
    delete query.question;
    delete query.quizId;
    // Without the quiz query params, the user will return to the activity container.
    router.replace({ pathname: router.pathname, query }, null, {
      shallow: true,
    });
  };

  const updateQuery = ({ params }) => {
    const href = {
      pathname,
      query: {
        ...query,
        ...params,
      },
    };
    router.replace(href, null, { shallow: true });
  };

  const renderQuizOverview = () => {
    if (!quizId || activityPanel !== "quizOverview") {
      return null;
    }

    return (
      <QuizOverviewContainer
        quizData={data}
        quizQuestionMetadata={quizQuestionMetadata}
      />
    );
  };

  const renderQuizPreview = () => {
    if (!quizId || activityPanel !== "quizPreview") {
      return null;
    }

    return (
      <QuizPreviewContainer
        quizData={data}
        quizQuestionMetadata={quizQuestionMetadata}
        isPresentModeActive={isPresentModeActive}
        language={language}
      />
    );
  };

  const backButtonEventProperties = {
    objectType: "Quiz",
    actionName: "Navigate",
    streamProperties: {
      content_type: "Article",
      description: "Navigate back to teacher activity container from quiz",
      content_id: levelId,
      object_id: quizId,
    },
  };

  const currentTab = activityPanel || QUIZ_OVERVIEW_PARAM;

  const eventProperties = (tab) => ({
    objectType: objectTypes.quiz,
    actionPrefix: actionPrefixes.initiate,
    actionName: actionNames.navigate,
    streamProperties: {
      content_type: "Article",
      description: `Navigate to quiz ${tab} screen`,
      content_id: levelId || "",
      object_id: String(quizId || ""),
    },
  });

  const trackTabClick = (tab) => {
    trackAction(eventProperties(tab));
  };

  const handleTabChange = (tabId) => {
    if (tabId === QUIZ_OVERVIEW_PARAM) {
      updateQuery({ params: { activityPanel: QUIZ_OVERVIEW_PARAM } });
    } else if (tabId === QUIZ_PREVIEW_PARAM) {
      updateQuery({
        params: { activityPanel: QUIZ_PREVIEW_PARAM, question: 1 },
      });
    }
  };

  return (
    <>
      <PanelHeader
        backButtonEventProperties={backButtonEventProperties}
        closeButtonEventProperties={closeButtonEventProperties}
        onClickBackButton={onClickBackButton}
        onClickCloseButton={onClickCloseButton}
        shouldFocusCloseButtonRef={shouldFocusCloseButtonRef}
        shouldRemoveBorderBottom
      >
        <div css={headerTextContainerCss}>
          <h6 css={subHeaderTextCss}>{ELA_QUIZ_SUBHEADER}</h6>
          <h2 css={headerTextCss}>Quiz</h2>
        </div>
      </PanelHeader>
      <Tabs onTabChange={handleTabChange} selectedTab={currentTab}>
        <Tabs.TabList aria-label="Quiz navigation">
          <Tabs.Tab
            tabId={QUIZ_OVERVIEW_PARAM}
            onClick={() => trackTabClick("overview")}
            dataProps={{
              "data-qa-selector": "reading-skills-quiz-overview-tab",
            }}
          >
            Overview
          </Tabs.Tab>
          <Tabs.Tab
            tabId={QUIZ_PREVIEW_PARAM}
            onClick={() => trackTabClick("preview")}
            dataProps={{
              "data-qa-selector": "reading-skills-quiz-preview-tab",
            }}
          >
            Preview
          </Tabs.Tab>
        </Tabs.TabList>
        <Tabs.TabPanels>
          <Tabs.TabPanel tabId={QUIZ_OVERVIEW_PARAM}>
            {renderQuizOverview()}
          </Tabs.TabPanel>

          <Tabs.TabPanel tabId={QUIZ_PREVIEW_PARAM}>
            {renderQuizPreview()}
          </Tabs.TabPanel>
        </Tabs.TabPanels>
      </Tabs>
    </>
  );
};

ReadingSkillsQuizContainer.propTypes = propTypes;

export default ReadingSkillsQuizContainer;
